import { fetch } from '../fetch';

/**
 * Backend JSON payloads. Only the fields the callers (the projects store) read
 * are declared; the rest of each payload is opaque (`unknown`) since this layer
 * never inspects it. `Response.json()` is typed `Promise<any>` by the DOM lib,
 * so each parse site casts once to the declared shape rather than leaking `any`.
 */
interface CreatedProject {
  id: string;
}
interface ModelVersionUpdate {
  updatedAt: string;
}

/**
 * Retrieve the projects of the current user.
 */
export function listProjects(): Promise<unknown> {
  return fetch('/api/projects', {}).then(response => {
    if (response.status === 200) {
      return response.json() as Promise<unknown>;
    } else {
      throw new Error('Could not fetch projects from the backend');
    }
  });
}

/**
 * Get the details of a model version.
 *
 * @param projectId id of the project
 * @param modelId id of the model
 * @param versionId id of the model version
 */
export function showModelVersion(
  projectId: string,
  modelId: string,
  versionId: string
): Promise<unknown> {
  return fetch(`/api/projects/${projectId}/models/${modelId}/versions/${versionId}`, {}).then(
    response => {
      if (response.status === 200) {
        return response.json() as Promise<unknown>;
      } else {
        throw new Error('Could not fetch model version from the backend');
      }
    }
  );
}

/**
 * Create a project (i.e. POST to `/api/projects`)
 *
 * @param project The payload to sent to the server
 */
export function createProject(project: unknown): Promise<CreatedProject> {
  return fetch('/api/projects', {
    method: 'POST',
    body: JSON.stringify(project),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json() as Promise<CreatedProject>;
    } else {
      throw new Error('Could not create project');
    }
  });
}

/**
 * Remove a project (i.e. DELETE to `/api/projects/:id`)
 *
 * @param id The id of the project to remove
 */
export function removeProject(id: string): Promise<boolean> {
  return fetch(`/api/projects/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.status === 204) {
      return true;
    } else {
      throw new Error('Could not remove project');
    }
  });
}

/**
 * Update the details of a model version.
 *
 * @param projectId id of the project
 * @param modelId id of the model
 * @param versionId id of the model version
 * @param updates the payload to sent to the server
 */
export function updateModelVersion(
  projectId: string,
  modelId: string,
  versionId: string,
  updates: unknown
): Promise<ModelVersionUpdate> {
  return fetch(`/api/projects/${projectId}/models/${modelId}/versions/${versionId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json() as Promise<ModelVersionUpdate>;
    } else {
      throw new Error('Could not save content');
    }
  });
}

/**
 * Export the content of a model version to an MXP-file
 *
 *
 * @param projectId id of the project
 * @param modelId id of the model
 * @param versionId id of the model version
 * @param images the images to add in the MXP.
 *   - name: the name for the image
 *   - blob: the blob for the image
 *
 * @returns a Promise that resolves to a Blob of the MXP-file (see
 * https://developer.mozilla.org/en-US/docs/Web/API/Blob)
 */
export function exportModelVersion(
  projectId: string,
  modelId: string,
  versionId: string,
  images: ReadonlyArray<{ name: string; blob: Blob }>
): Promise<Blob> {
  const formData = new FormData();
  for (const image of images) {
    formData.append('images', image.blob, image.name);
  }
  return fetch(`/api/projects/${projectId}/models/${modelId}/versions/${versionId}/mxp`, {
    method: 'POST',
    body: formData,
  }).then(response => {
    if (response.status === 200) {
      return response.blob();
    } else {
      throw new Error('Could not export the model version');
    }
  });
}

/**
 * Update the model settings
 *
 * @param projectId id of the project
 * @param modelId id of the model
 * @param updates the payload to sent to the server
 */
export function updateModelSettings(
  projectId: string,
  modelId: string,
  updates: unknown
): Promise<ModelVersionUpdate> {
  return fetch(`/api/projects/${projectId}/models/${modelId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json() as Promise<ModelVersionUpdate>;
    } else {
      throw new Error('Could not save model settings');
    }
  });
}

/**
 * Create a new model version.
 *
 * @param projectId id of the project
 * @param modelId id of the model
 * @param version the payload to sent to the server
 */
export function createModelVersion(
  projectId: string,
  modelId: string,
  version: unknown
): Promise<unknown> {
  return fetch(`/api/projects/${projectId}/models/${modelId}/versions/`, {
    method: 'POST',
    body: JSON.stringify(version),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then(response => {
    if (response.status === 200) {
      return response.json() as Promise<unknown>;
    } else {
      throw new Error('Could not create model version');
    }
  });
}
